import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { riderAPI } from '../utils/api';
import { UserPlus, Horse, Trash, X, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const RiderProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [managedRiders, setManagedRiders] = useState([]);
  const [horses, setHorses] = useState([]);
  const [showAddRider, setShowAddRider] = useState(false);
  const [showAddHorse, setShowAddHorse] = useState(false);
  const [riderForm, setRiderForm] = useState({ name: '', email: '', relationship: '' });
  const [horseForm, setHorseForm] = useState({ name: '', breed: '', notes: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [ridersRes, horsesRes] = await Promise.all([
        riderAPI.getManagedRiders(),
        riderAPI.getHorses(),
      ]);
      setManagedRiders(ridersRes.data);
      setHorses(horsesRes.data);
    } catch { /* ignore */ }
  };

  const handleAddRider = async () => {
    if (!riderForm.name.trim()) { toast.error('Rider name is required'); return; }
    try {
      await riderAPI.addManagedRider(riderForm);
      toast.success(`${riderForm.name} added`);
      setRiderForm({ name: '', email: '', relationship: '' });
      setShowAddRider(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add rider'); }
  };

  const handleAddHorse = async () => {
    if (!horseForm.name.trim()) { toast.error('Horse name is required'); return; }
    try {
      await riderAPI.addHorse(horseForm);
      toast.success(`${horseForm.name} added`);
      setHorseForm({ name: '', breed: '', notes: '' });
      setShowAddHorse(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add horse'); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'rider') await riderAPI.deleteManagedRider(deleteConfirm.id);
      else await riderAPI.deleteHorse(deleteConfirm.id);
      toast.success(`${deleteConfirm.name} removed`);
      setDeleteConfirm(null);
      loadData();
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-1" style={{ fontFamily: 'Outfit' }} data-testid="rider-profile-title">My Profile</h1>
        <p className="text-[#9E9E9E] text-sm mb-8">{user?.name} &bull; {user?.email}</p>

        {/* My Horses */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Horse size={22} weight="duotone" className="text-[#FF9500]" />
              <h2 className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                My Horses <span className="text-[#9E9E9E] text-sm font-normal">({horses.length})</span>
              </h2>
            </div>
            <button
              onClick={() => setShowAddHorse(!showAddHorse)}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#FF9500]/10 border border-[#FF9500]/30 text-[#FF9500] text-xs font-bold rounded-lg hover:bg-[#FF9500]/20 transition-colors"
              data-testid="add-horse-btn"
            >
              <Plus size={14} weight="bold" /> Add Horse
            </button>
          </div>

          {showAddHorse && (
            <div className="mb-4 bg-[#161616] border border-[#FF9500]/20 rounded-lg p-4" data-testid="add-horse-form">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[#FF9500]">New Horse</h4>
                <button onClick={() => setShowAddHorse(false)} className="p-1 hover:bg-[#262626] rounded"><X size={16} className="text-[#9E9E9E]" /></button>
              </div>
              <div className="space-y-3">
                <input
                  type="text" value={horseForm.name} onChange={(e) => setHorseForm({ ...horseForm, name: e.target.value })}
                  placeholder="Horse name *" autoFocus
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500] placeholder:text-[#555]"
                  data-testid="horse-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddHorse(); }}
                />
                <input
                  type="text" value={horseForm.breed} onChange={(e) => setHorseForm({ ...horseForm, breed: e.target.value })}
                  placeholder="Breed (optional)"
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500] placeholder:text-[#555]"
                  data-testid="horse-breed-input"
                />
                <button onClick={handleAddHorse} className="px-4 py-2.5 bg-[#FF9500] text-white rounded-md text-sm font-semibold hover:bg-[#FF9500]/80 transition-colors" data-testid="horse-submit-btn">Add Horse</button>
              </div>
            </div>
          )}

          {horses.length === 0 ? (
            <div className="bg-[#161616] border border-[#262626] rounded-lg p-6 text-center">
              <Horse size={36} className="text-[#262626] mx-auto mb-2" />
              <p className="text-[#9E9E9E] text-sm">No horses saved yet</p>
              <p className="text-[#555] text-xs mt-1">Add your horses here and pick them when entering shows</p>
            </div>
          ) : (
            <div className="space-y-2">
              {horses.map((h) => (
                <div key={h.id} className="bg-[#161616] border border-[#262626] rounded-lg px-4 py-3 flex items-center justify-between group" data-testid={`horse-${h.id}`}>
                  <div>
                    <p className="text-white font-semibold text-sm">{h.name}</p>
                    {h.breed && <p className="text-[#9E9E9E] text-xs">{h.breed}</p>}
                  </div>
                  <button
                    onClick={() => setDeleteConfirm({ id: h.id, name: h.name, type: 'horse' })}
                    className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-[#FF3B30]/10 rounded transition-all"
                    data-testid={`delete-horse-${h.id}`}
                  >
                    <Trash size={14} className="text-[#FF3B30]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Managed Riders */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <UserPlus size={22} weight="duotone" className="text-[#0096FF]" />
              <h2 className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                My Riders <span className="text-[#9E9E9E] text-sm font-normal">({managedRiders.length})</span>
              </h2>
            </div>
            <button
              onClick={() => setShowAddRider(!showAddRider)}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#0096FF]/10 border border-[#0096FF]/30 text-[#0096FF] text-xs font-bold rounded-lg hover:bg-[#0096FF]/20 transition-colors"
              data-testid="add-managed-rider-btn"
            >
              <Plus size={14} weight="bold" /> Add Rider
            </button>
          </div>

          {showAddRider && (
            <div className="mb-4 bg-[#161616] border border-[#0096FF]/20 rounded-lg p-4" data-testid="add-rider-form">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[#0096FF]">New Managed Rider</h4>
                <button onClick={() => setShowAddRider(false)} className="p-1 hover:bg-[#262626] rounded"><X size={16} className="text-[#9E9E9E]" /></button>
              </div>
              <p className="text-[#555] text-xs mb-3">Add a family member or friend you enter shows for</p>
              <div className="space-y-3">
                <input
                  type="text" value={riderForm.name} onChange={(e) => setRiderForm({ ...riderForm, name: e.target.value })}
                  placeholder="Rider name *" autoFocus
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] placeholder:text-[#555]"
                  data-testid="managed-rider-name-input"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="email" value={riderForm.email} onChange={(e) => setRiderForm({ ...riderForm, email: e.target.value })}
                    placeholder="Email (optional)"
                    className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] placeholder:text-[#555]"
                    data-testid="managed-rider-email-input"
                  />
                  <select
                    value={riderForm.relationship} onChange={(e) => setRiderForm({ ...riderForm, relationship: e.target.value })}
                    className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] appearance-none"
                    data-testid="managed-rider-relationship"
                  >
                    <option value="">Relationship</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Child">Child</option>
                    <option value="Parent">Parent</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Friend">Friend</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <button onClick={handleAddRider} className="px-4 py-2.5 bg-[#0096FF] text-white rounded-md text-sm font-semibold hover:bg-[#0096FF]/80 transition-colors" data-testid="rider-submit-btn">Add Rider</button>
              </div>
            </div>
          )}

          {managedRiders.length === 0 ? (
            <div className="bg-[#161616] border border-[#262626] rounded-lg p-6 text-center">
              <UserPlus size={36} className="text-[#262626] mx-auto mb-2" />
              <p className="text-[#9E9E9E] text-sm">No managed riders yet</p>
              <p className="text-[#555] text-xs mt-1">Add family members or friends you enter shows for</p>
            </div>
          ) : (
            <div className="space-y-2">
              {managedRiders.map((r) => (
                <div key={r.id} className="bg-[#161616] border border-[#262626] rounded-lg px-4 py-3 flex items-center justify-between group" data-testid={`managed-rider-${r.id}`}>
                  <div>
                    <p className="text-white font-semibold text-sm">{r.name}</p>
                    <div className="flex items-center gap-2">
                      {r.relationship && <span className="text-[#0096FF] text-xs">{r.relationship}</span>}
                      {r.email && <span className="text-[#9E9E9E] text-xs">{r.email}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => setDeleteConfirm({ id: r.id, name: r.name, type: 'rider' })}
                    className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-[#FF3B30]/10 rounded transition-all"
                    data-testid={`delete-managed-rider-${r.id}`}
                  >
                    <Trash size={14} className="text-[#FF3B30]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="delete-profile-item-modal">
            <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Remove {deleteConfirm.type === 'rider' ? 'Rider' : 'Horse'}</h3>
            <p className="text-[#9E9E9E] text-sm mb-5">Remove <span className="text-white font-semibold">{deleteConfirm.name}</span> from your profile?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 transition-colors" data-testid="confirm-delete-profile-item">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiderProfile;
